import { useState } from 'react';
import { Printer, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { Button3D } from '@/components/ui/button-3d';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useFeature } from '@/hooks/useFeature';
import { downloadQuotePDF, type PDFQuoteData } from '@/lib/pdfGenerator';
import {
  THERMAL_FORMATS,
  THERMAL_SPECS,
  readThermalPreference,
  writeThermalPreference,
  thermalSpec,
  type ThermalFormat,
} from '@/lib/thermalPaper';

interface ThermalPrintButtonProps {
  /**
   * Produz e entrega o cupom na largura escolhida.
   *
   * A responsabilidade fica com a TELA, não com o botão, porque as telas não
   * concordam sobre o que precisa acontecer antes de imprimir: o PDV imprime a
   * venda já fechada, enquanto o orçamento SALVA o que está em edição antes de
   * gerar (e ainda pode criar o cliente no caminho). Um `buildData` genérico
   * obrigaria o orçamento a imprimir um documento diferente do que ficou
   * gravado. As telas simples chamam `downloadThermalPdf` aqui de baixo.
   *
   * Devolver `false` significa "não gerei, e já avisei o usuário por conta
   * própria" — é o caso da validação do orçamento, que barra com um
   * `toast.warning` e retorna sem lançar. Sem esse canal, o botão anunciaria
   * "cupom gerado" logo abaixo de "Informe o nome do cliente".
   */
  onPrint: (format: ThermalFormat) => Promise<boolean | void> | boolean | void;
  className?: string;
  label?: string;
}

/**
 * Atalho para as telas cujo cupom é só "pegue estes dados e gere": monta o nome
 * do arquivo com o sufixo da bobina e delega ao gerador.
 */
export async function downloadThermalPdf(
  data: PDFQuoteData,
  filenameBase: string,
  format: ThermalFormat,
): Promise<void> {
  const spec = thermalSpec(format);
  await downloadQuotePDF(data, `${filenameBase}-${spec.paperWidth}mm-${Date.now()}.pdf`, {
    paper: format,
  });
}

/**
 * "Imprimir térmica" — gera o cupom em bobina e entrega ao sistema.
 *
 * ─── COMO A IMPRESSÃO ACONTECE DE FATO ───────────────────────────────────────
 *
 * O app NÃO fala ESC/POS com a impressora. Ele gera um PDF já no tamanho da
 * bobina e entrega ao Android (o mesmo caminho de `downloadQuotePDF`: grava em
 * Documentos e abre com o `FileOpener`, que mostra o "Abrir com"). Daí o usuário
 * escolhe o serviço de impressão ou o app da impressora, que recebe uma página
 * do tamanho certo e imprime 1:1.
 *
 * A alternativa — Bluetooth + ESC/POS direto — dá mais fidelidade, mas exige
 * plugin nativo, permissão `BLUETOOTH_CONNECT`, UX de pareamento e um dialeto
 * por marca (ESC/POS × TSPL × CPCL). Fica para quando houver demanda medida.
 *
 * A largura é lembrada POR APARELHO (ver `thermalPaper.ts`): o botão imprime
 * direto, e o seletor ao lado mostra a bobina atual e permite trocar.
 */
export function ThermalPrintButton({
  onPrint,
  className,
  label = 'Imprimir térmica',
}: ThermalPrintButtonProps) {
  const thermalPrinting = useFeature('thermal_printing');
  const [format, setFormat] = useState<ThermalFormat>(() => readThermalPreference());
  const [printing, setPrinting] = useState(false);

  const handlePrint = async (chosen: ThermalFormat = format) => {
    if (!thermalPrinting.require()) return;
    setPrinting(true);
    try {
      const produced = await onPrint(chosen);
      if (produced === false) return;
      // O próximo passo não é óbvio (o Android abre um "Abrir com", não imprime
      // sozinho), então a mensagem diz o que fazer em vez de só "pronto".
      const spec = thermalSpec(chosen);
      toast.success(
        Capacitor.isNativePlatform()
          ? `Cupom de ${spec.label} gerado. Escolha o app da sua impressora para imprimir.`
          : `Cupom de ${spec.label} baixado.`,
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Não foi possível gerar o cupom.';
      toast.error(message);
    } finally {
      setPrinting(false);
    }
  };

  const handleChooseFormat = (chosen: ThermalFormat) => {
    setFormat(chosen);
    writeThermalPreference(chosen);
    void handlePrint(chosen);
  };

  return (
    <div className={`flex gap-2 ${className ?? ''}`}>
      <Button3D
        type="button"
        className="flex-1"
        onClick={() => void handlePrint()}
        disabled={printing}
      >
        <Printer className="mr-2 h-4 w-4" />
        {printing ? 'Gerando...' : label}
      </Button3D>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* O rótulo do gatilho É a bobina atual: o usuário vê em que largura
              vai sair sem precisar abrir nada, e trocar é um toque. */}
          <Button3D
            type="button"
            color="gray"
            className="px-3"
            disabled={printing}
            aria-label="Largura da bobina"
          >
            {THERMAL_SPECS[format].label}
          </Button3D>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Largura da bobina</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {THERMAL_FORMATS.map((option) => (
            <DropdownMenuItem key={option} onSelect={() => handleChooseFormat(option)}>
              <Check
                className={`mr-2 h-4 w-4 ${option === format ? 'opacity-100' : 'opacity-0'}`}
              />
              <span className="flex flex-col">
                <span className="font-medium">{THERMAL_SPECS[option].label}</span>
                <span className="text-xs text-muted-foreground">
                  {THERMAL_SPECS[option].description}
                </span>
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
